/**
 * contractor-columns.tsx — wspólna konfiguracja KOLUMN listy kontrahentów.
 *
 * Jedno źródło prawdy dla dwóch ekranów, które pokazują kontrahentów jako tabelę z kolumnami „jak
 * na liście": strony `Contractors` oraz sekcji kokpitu decyzji (`pages/processes/decision-sections`,
 * „Grupuj według: Kartoteka"). Zmiana tutaj jest widoczna na obu — kolumny nie mogą się rozjechać.
 *
 * Zawartość: rejestr nadpisań renderu kolumn rdzennych (`buildContractorOverrides` — nazwa z
 * podpisem „nazwa skrócona" i linkiem do detalu, typ podmiotu, status jako plakietka), slugi
 * obsługiwane osobno (`CONTRACTORS_EXCLUDE_SLUGS`), argumenty kolumny „Firma" (firma macierzysta —
 * `CONTRACTOR_COMPANY_COLUMN_ARGS`), slugi dołączane do `?columns=` (`CONTRACTOR_ALWAYS_SLUGS`,
 * `CONTRACTOR_COLUMN_COMPANIONS`), mapa id kolumny → slug backendu (`CONTRACTOR_COLUMN_ID_TO_SLUG`)
 * i typ wiersza listy (`ContractorRow`). Kolumny rdzenne/dodatkowe buduje katalog pól (`buildCoreColumns`/
 * `buildExtColumns` z `ext-list-columns`). Kontrahenci nie mają kolumn procesu ani odwołań
 * dokumentu (opiekun/źródło) na liście.
 *
 * Etykiety kodów enumów składają klucze i18n z kodu (`...objectType.${code}`) w
 * `lib/core-field-option-labels.ts` — to ŚWIADOMY wyjątek od reguły z `ksef-jpk-markers.ts`
 * (literał `t()` per gałąź): strażnikiem jest test, który sprawdza tłumaczenie KAŻDEGO kodu w
 * `pl.json` (skaner `check:i18n` dynamicznych kluczy nie widzi).
 */

import { Link } from 'react-router-dom';
import {
  CONTRACTOR_OBJECT_TYPES,
  CONTRACTOR_STATUSES,
  CONTRACTOR_VAT_STATUSES,
  type ContractorStatus,
  RECORD_LANGUAGES,
} from '@saas/shared';
import { ArrowLeftRight } from 'lucide-react';

import { Badge, type BadgeTone } from '@/components/Badge';
import { COMPOSITE_TEXT_FILTER_OPERATORS } from '@/components/configurable-table/filter-row/filter-operators';
import {
  contractorObjectTypeLabel,
  contractorStatusLabel,
  contractorVatStatusLabel,
  recordLanguageLabel,
} from '@/lib/core-field-option-labels';
import {
  buildStaticSelectOverride,
  type ColumnCompanionRule,
  type CoreColumnOverride,
  isFieldFilterable,
} from '@/lib/ext-list-columns';

/**
 * Slugi rdzenne obsługiwane OSOBNO (dedykowany budowniczy `buildCompanyColumn`, albo BEZ
 * kolumny na tej liście), więc pomijane przez `buildCoreColumns` — inaczej powstałaby zdublowana
 * kolumna „Firma", albo katalog wypchnąłby na listę kolumny referencyjne, których dzisiejsza
 * (pre-migration) strona nigdy nie renderowała. `company_id` → `buildCompanyColumn` (firma
 * macierzysta kontrahenta żyje w `home_company_id`/`homeCompanyId`, patrz
 * `CONTRACTOR_COMPANY_COLUMN_ARGS`). `owner_user_id`/`assigned_user_id` — kontrahenci nie mają
 * dziś dedykowanej kolumny „Opiekun"/„Przypisany" na liście.
 */
export const CONTRACTORS_EXCLUDE_SLUGS: ReadonlySet<string> = new Set([
  'company_id',
  'owner_user_id',
  'assigned_user_id',
]);

/**
 * Argumenty `buildCompanyColumn` kontrahentów: firma macierzysta kontrahenta żyje w
 * `home_company_id`/`homeCompanyId` (domyślne `companyId`/`company_id` to kontrakt dokumentów).
 */
export const CONTRACTOR_COMPANY_COLUMN_ARGS = {
  accessor: 'homeCompanyId',
  fieldSlug: 'home_company_id',
} as const;

/**
 * Slugi dołączane do `?columns=` ZAWSZE, niezależnie od widocznych kolumn: `source` nie jest osobną
 * kolumną — dociągamy je zawsze, bo chip „Symfonia" przy nazwie kontrahenta musi renderować się
 * niezależnie od widoczności kolumn.
 */
export const CONTRACTOR_ALWAYS_SLUGS: readonly string[] = ['source'];

/**
 * Reguły towarzysza dla `?columns=`: nazwa→krótka nazwa. Podpis „krótka nazwa" pod nazwą
 * kontrahenta czyta `row.shortName`, a to nie jest osobna kolumna — bez wymuszenia zniknąłby w
 * projekcji.
 */
export const CONTRACTOR_COLUMN_COMPANIONS: readonly ColumnCompanionRule[] = [
  { whenSlug: 'name', addSlug: 'short_name' },
];

/**
 * Mapa id kolumny → slug backendu dla `?columns=` — część STAŁA: kolumny spoza `buildCoreColumns`
 * (firma ma id `company`, kategoria `category`). Kolumny z katalogu (`buildCoreColumns`) dopisuje
 * wołający pętlą `id = slug`.
 */
export const CONTRACTOR_COLUMN_ID_TO_SLUG: Readonly<Record<string, string>> = {
  company: 'company_id',
  category: 'category',
};

// Tonacja plakietki per status kontrahenta (enum 4-wartościowy: active/prospect/to_verify/
// blocked). Aktywny = zielony, prospekt = niebieski, do weryfikacji = bursztyn, zablokowany =
// czerwony. Spójne z plakietkami statusu w reszcie apki; daje czytelne kolumny na Kanbanie.
// `satisfies` pilnuje, żeby mapa pokrywała każdy kod z listy w `@saas/shared`; typ po stronie
// odczytu zostaje indeksowalny dowolnym tekstem (wartość spoza listy → `neutral`).
const CONTRACTOR_STATUS_TONE: Readonly<Record<string, BadgeTone>> = {
  active: 'success',
  prospect: 'info',
  to_verify: 'warning',
  blocked: 'danger',
} satisfies Record<ContractorStatus, BadgeTone>;

// Kolor kolumny Kanban per status — zgodny z tonacją plakietki. Lista kodów (kolejność = pipeline
// CRM) pochodzi z `CONTRACTOR_STATUSES` w `@saas/shared`; etykiety dokłada miejsce użycia.
const CONTRACTOR_STATUS_COLOR: Record<ContractorStatus, string> = {
  active: '#16a34a',
  prospect: '#2563eb',
  to_verify: '#d97706',
  blocked: '#dc2626',
};

// Zamknięte enumy kontrahenta wystawione w kolumnach listy jako listy wyboru (typ podmiotu, status VAT,
// preferowany język) biorą kody z `@saas/shared` (`CONTRACTOR_OBJECT_TYPES`, `CONTRACTOR_VAT_STATUSES`,
// `RECORD_LANGUAGES`) — te same listy co formularz i walidacja tras. Etykiety kodów: jedno miejsce,
// `lib/core-field-option-labels.ts` (kod spoza listy — dane historyczne — wraca surowy). Test
// `contractor-columns.test.tsx` pilnuje, że każdy kod ma w `pl.json` prawdziwe tłumaczenie.

/**
 * `id` (klucz React) i `name` (kolumna `hideable: false`, zawsze widoczna) są jedynymi polami
 * GWARANTOWANYMI w każdej odpowiedzi projekcji (`?columns=`) — reszta zależy od aktualnie
 * widocznych kolumn. Tryb legacy (bez `?columns=`) zwraca pełny wiersz, ale trasa i tak ODCINA
 * `ext` w tym trybie (patrz `route.ts`), więc `ext` zostaje opcjonalny niezależnie od trybu.
 * Typ wiersza listy kontrahentów; wiersz sekcji kokpitu decyzji = ten wiersz + kroki decyzji.
 */
export interface ContractorRow {
  id: string;
  name: string;
  shortName?: string | null;
  taxId?: string | null;
  status?: string;
  objectType?: string;
  ownerUserId?: string | null;
  assignedUserId?: string | null;
  homeCompanyId?: string;
  phone?: string | null;
  email?: string | null;
  /** Pochodzenie rekordu: 'symfonia' (import z ERP), 'import', 'mf', null=ręcznie.
   *  Backend zwraca zawsze (wymuszone `alwaysSlugs`, niezależnie od `?columns=`). Steruje chipem „Symfonia". */
  source?: string | null;
  /** Nazwa kategorii z `record_categories` (LEFT JOIN w deskryptorze) — kolumna „Kategoria". */
  categoryName?: string | null;
  /**
   * Wartości pól dodatkowych (ext) — obecne TYLKO gdy backend odpowiada w trybie
   * projekcji `?columns=` i przynajmniej jedno aktywne pole ext zostało zażądane
   * (klucz `ext` jest wtedy pominięty gdy brak zażądanych pól ext, patrz kontrakt
   * `GET /api/contractors?columns=`).
   */
  ext?: Record<string, unknown>;
}

/**
 * Rejestr nadpisań kolumn rdzennych kontrahentów (per-kartoteka) — render bogaty tam, gdzie
 * generyk (typ→komórka) nie odtwarza wiernie dotychczasowy wygląd. NIP/telefon/email NIE są
 * tu — generyk (`renderExtFieldValue`, varchar/email) je odtwarza 1:1 (dash gdy puste, plain
 * text inaczej). Sortowalność: `ctx.resolveSortable(ctx.field)` (ta sama serwerowa reguła co
 * generyk — id kolumny = slug na tej liście, żadnych remapów sortu). Klucze = slugi pól z
 * katalogu (`system_module_fields.slug`).
 *
 * `withMenuItem` — dokleja `?mi=<id>` (pozycja menu) do linku nazwy; strona listy podaje
 * `useWithMenuItem()`, sekcja kokpitu decyzji tożsamość (`(path) => path`).
 */
export function buildContractorOverrides(
  withMenuItem: (path: string) => string,
): Record<string, CoreColumnOverride<ContractorRow>> {
  return {
    // Nazwa + podpis „nazwa skrócona" pod spodem (companion `short_name`, patrz
    // `CONTRACTOR_COLUMN_COMPANIONS` — short_name NIE jest osobną kolumną) + link do detalu.
    // Zawsze widoczna i niehideable: bez niej tabela traci sens.
    name: ({ field, accessor, resolveSortable, t }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 280,
      hideable: false,
      sortable: resolveSortable?.(field) ?? false,
      // Filtr kolumny ZŁOŻONEJ „Nazwa" — grupa OR: syntetyczny slug `name_full` (backend rozwija
      // na `name`/`short_name`). `conditionOnly` — slug filtra-tylko. `orGroupFields` wygasza
      // wzajemnie z ewentualnymi osobnymi kolumnami nazwy/skrótu.
      headerFilter: {
        category: 'text',
        fieldSlug: 'name_full',
        conditionOnly: true,
        operators: COMPOSITE_TEXT_FILTER_OPERATORS,
        orGroupFields: ['name', 'short_name'],
      },
      renderCell: (_value, row) => (
        <div onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1.5">
            <Link
              to={withMenuItem(`/contractors/${row.id}`)}
              className="font-medium text-accent-700 hover:underline dark:text-accent-300"
            >
              {row.name}
            </Link>
            {row.source === 'symfonia' && (
              <Badge
                color="accent"
                size="sm"
                icon={ArrowLeftRight}
                data-testid={`contractors-row-${row.id}-source-symfonia`}
              >
                {t('tenant.contractors.source.symfonia')}
              </Badge>
            )}
          </div>
          {row.shortName && <div className="text-xs text-text-tertiary">{row.shortName}</div>}
        </div>
      ),
    }),
    // Typ podmiotu — enum zamknięty (company/sole_proprietorship/person), bez słownika katalogu:
    // lista wyboru w wierszu filtrów (wartość = kod z bazy) i etykieta w komórce z jednej funkcji
    // (`buildStaticSelectOverride`); styl wygaszony (muted) jak dotąd. Bez opcji wiersz filtrów
    // spadał na martwe pole „Szukaj…", a komórka pokazywała surowy kod (`sole_proprietorship`).
    object_type: buildStaticSelectOverride<ContractorRow>({
      codes: CONTRACTOR_OBJECT_TYPES,
      labelOf: contractorObjectTypeLabel,
      defaultWidth: 140,
      cellClassName: 'text-text-tertiary',
    }),
    // Status VAT i preferowany język — zamknięte enumy (CHECK w bazie / `pl`,`en`), bez słownika
    // katalogu, więc generyk dawał PUSTĄ komórkę filtra i surowy kod w komórce.
    vat_status: buildStaticSelectOverride<ContractorRow>({
      codes: CONTRACTOR_VAT_STATUSES,
      labelOf: contractorVatStatusLabel,
    }),
    preferred_language: buildStaticSelectOverride<ContractorRow>({
      codes: RECORD_LANGUAGES,
      labelOf: recordLanguageLabel,
    }),
    // Status — enum zamknięty (4 wartości), plakietka + etykieta i18n. Wartość pusta/nullish NIE
    // dostaje plakietki (byłaby to plakietka z kluczem i18n nierozwiązanym) — myślnik jak reszta
    // generyku. Wartość spoza słownika (nieznana) pokazuje surową wartość (`defaultValue`), nie
    // goły klucz i18n.
    // Filtr tylko wtedy, gdy serwer deklaruje pole (`isFieldFilterable`, jak w pozostałych listach
    // wyboru); `filterable: false` — ta kolumna ma listę wyboru, nigdy pole „Szukaj…". Uwaga: z
    // `headerFilter` korzysta też Kanban kontrahentów (pole grupujące i etykiety torów) — `status`
    // jest na whiteliście deskryptora listy, więc serwer deklaruje go od pierwszej odpowiedzi.
    status: ({ field, accessor, t, resolveSortable, resolveFilterable }) => ({
      id: field.slug,
      accessor,
      header: field.name,
      defaultVisible: true,
      defaultWidth: 130,
      sortable: resolveSortable?.(field) ?? false,
      filterable: false,
      ...(isFieldFilterable(field, resolveFilterable)
        ? {
            headerFilter: {
              category: 'select' as const,
              fieldSlug: 'status',
              // Statyczne opcje (4 statusy) — dają dropdown filtra ORAZ kolumny tablicy Kanban
              // (KanbanBoard grupuje po pierwszej kolumnie select z opcjami). Kolory = tonacja
              // plakietki (patrz CONTRACTOR_STATUS_TONE). Kolejność = pipeline CRM.
              options: CONTRACTOR_STATUSES.map((code) => ({
                value: code,
                label: contractorStatusLabel(code, t),
                color: CONTRACTOR_STATUS_COLOR[code],
              })),
              transformLabel: (value: string | null) =>
                value ? contractorStatusLabel(value, t) : t('common.lists.emptyValue'),
            },
          }
        : {}),
      renderCell: (value) =>
        value == null || value === '' ? (
          <span className="text-text-tertiary">—</span>
        ) : (
          // Plakietka statusu w kolorze semantycznym (aktywny=zielony, prospekt=niebieski,
          // do weryfikacji=bursztyn, zablokowany=czerwony) — spójnie z plakietkami statusu
          // w reszcie apki i czytelne kolumny na tablicy Kanban.
          <Badge tone={CONTRACTOR_STATUS_TONE[String(value)] ?? 'neutral'}>
            {contractorStatusLabel(String(value), t)}
          </Badge>
        ),
    }),
  };
}
